import { useMemo, useState } from 'react';
import { Calculator, ArrowRight, X as XIcon, CheckCircle, CaretDown, CaretUp, WarningCircle } from '@phosphor-icons/react';

/**
 * Embedded savings calculator for the MigrationCrunch landing page.
 * Anchored at /migrate#calculator.
 *
 * Pricing assumptions (grounded in actual 2025/2026 market rates — see
 * web research notes in /app/marketing/marketer-cheat-sheet-migrationcrunch.md
 * and /app/marketing/competitive-comparison-matrix.md):
 *
 *   - Agency (done-for-you consulting): $1,500 base + $0.35/contact,
 *     soft-capped at $100,000 for the SMB/mid-market view. Enterprise
 *     integrators (Slalom, Deloitte) charge $50k–$150k for 500k+ migrations;
 *     $100k is the realistic ceiling for the buyer comparing alternatives.
 *   - DIY tool + cleanup labor: $499 base + $0.20/contact (NO CAP). This
 *     models the real per-record pricing of Import2/MigrateMyCRM/Trujay
 *     which charges per record (not per contact). Each contact carries
 *     ~3–8 records (emails, notes, tasks) so per-contact effective rate
 *     lands $0.15–$0.35 depending on history density. We use $0.20 as
 *     the SMB average. No cap because real quotes scale linearly past
 *     100k contacts (e.g., MigrateMyCRM has quoted $20k+ for 100k active).
 *   - MigrationCrunch: flat tier price from /app/backend/migration_pro_routes.py
 */

const TIERS = [
  { max: 12_500, price: 599, name: 'Starter Pro' },
  { max: 100_000, price: 1799, name: 'Growth AI' },
  { max: 500_000, price: 4499, name: 'Enterprise AI' },
  { max: Infinity, price: 7999, name: 'Unlimited' },
];

function pickTier(count) {
  return TIERS.find((t) => count <= t.max);
}

function agencyCost(count) {
  // $1,500 base + $0.35/contact, capped at $100,000 (SMB/mid-market view)
  return Math.min(100_000, 1_500 + count * 0.35);
}

function diyCost(count) {
  // $499 base + $0.20/contact — NO cap. Per-record competitors scale
  // linearly past 100k contacts; capping would mis-represent the market.
  return 499 + count * 0.20;
}

function fmtMoney(n) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function fmtCount(n) {
  return Number(n).toLocaleString('en-US');
}

export default function MigrationSavingsCalculator() {
  const [count, setCount] = useState(10_000);
  const [showMath, setShowMath] = useState(false);

  const numbers = useMemo(() => {
    const safe = Math.max(100, Math.min(1_000_000, Number(count) || 0));
    const agency = agencyCost(safe);
    const diy = diyCost(safe);
    const tier = pickTier(safe);
    const mc = tier.price;
    const compared = Math.max(agency, diy);
    const savings = Math.max(0, compared - mc);
    const savingsPct = compared > 0 ? Math.round((savings / compared) * 100) : 0;
    return { agency, diy, mc, tier, savings, savingsPct, safe };
  }, [count]);

  const onSliderChange = (e) => setCount(Number(e.target.value));
  const onNumberChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setCount(raw === '' ? '' : Number(raw));
  };

  return (
    <section
      id="calculator"
      className="px-6 py-16 border-t border-white/10 bg-gradient-to-b from-[#0a0e27] via-[#0F0F1A] to-[#0a0e27]"
      data-testid="mc-calculator-section"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-3">
            <Calculator size={12} weight="fill" /> Savings calculator
          </div>
          <h2 className="text-3xl md:text-5xl font-black mb-3 text-white">
            Do the math yourself.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-base">
            Slide the contact count to your number. We&apos;ll show what an agency
            charges, what a DIY tool plus cleanup labor costs, and what
            MigrationCrunch costs flat. No marketing math — just the actual prices.
          </p>
        </div>

        {/* Input control */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-6 md:p-8 mb-8" data-testid="mc-calc-input-block">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs uppercase tracking-widest text-white/60 font-bold">
              How many contacts are you migrating?
            </label>
            <span className="text-xs text-white/40">
              {numbers.tier.name} tier
            </span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={100}
              max={500_000}
              step={100}
              value={typeof count === 'number' ? count : 100}
              onChange={onSliderChange}
              className="flex-1 accent-[#F39C12] h-2"
              data-testid="mc-calc-slider"
            />
            <div className="flex items-baseline gap-1 bg-white/5 border border-white/10 rounded px-3 py-1.5">
              <input
                type="text"
                inputMode="numeric"
                value={typeof count === 'number' ? fmtCount(count) : ''}
                onChange={onNumberChange}
                className="bg-transparent text-white text-2xl md:text-3xl font-black w-32 md:w-44 text-right outline-none focus:text-[#F39C12]"
                data-testid="mc-calc-number-input"
              />
              <span className="text-xs text-white/40 uppercase tracking-wider">contacts</span>
            </div>
          </div>
          {/* Ghost-number: what the SAME database looks like on Import2's
              invoice (contacts × 15 records/contact avg). Makes the
              sticker-price gap immediately visceral without any math. */}
          {typeof count === 'number' && count > 0 && (
            <div
              className="mt-2 text-right text-[11px] text-white/40 italic"
              data-testid="mc-calc-records-ghost"
            >
              &asymp; <span className="text-white/60 font-mono not-italic">{fmtCount(count * 15)}</span>{' '}
              records on Import2&rsquo;s / MigrateMyCRM&rsquo;s invoice
            </div>
          )}
          <div className="flex justify-between text-[10px] text-white/40 mt-2 px-1">
            <span>100</span>
            <span>50k</span>
            <span>100k</span>
            <span>250k</span>
            <span>500k+</span>
          </div>

          {/* Contacts-vs-records explainer — the whole reason our number
              looks so much smaller than what competitors would quote for
              the "same" migration. Sits inside the input card so it's
              impossible to miss when the user is looking at the number. */}
          <div className="mt-5 pt-4 border-t border-white/10" data-testid="mc-calc-records-explainer">
            <p className="text-[11px] uppercase tracking-widest text-[#F39C12] font-bold mb-1.5">
              Why we count contacts, not records
            </p>
            <p className="text-xs text-white/70 leading-relaxed">
              Import2, MigrateMyCRM, and Trujay charge you by <em>records</em> &mdash;
              every note, email, call log, task, and meeting counts as one.
              A typical contact carries about <strong className="text-white">15 records</strong>
              of history, so what looks like 10,000 contacts on your end
              is 150,000 records on their invoice (and a much bigger
              price tier). <strong className="text-white">We charge by
              contacts.</strong> Move the slider by the number of <em>people</em>{' '}
              in your database &mdash; we&rsquo;ll bring everything attached
              to each one along for the ride.
            </p>
          </div>
        </div>

        {/* Apples-to-apples banner */}
        <div className="bg-white/5 border border-white/10 rounded-md px-4 py-3 mb-4 flex items-start gap-3" data-testid="mc-calc-apples-banner">
          <CheckCircle size={18} weight="fill" className="text-[#F39C12] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-white/80 leading-relaxed">
            <strong className="text-white">All three are priced for the same outcome</strong> —
            a clean, structured CRM where every phone number, email, and family
            detail from the old Notes column lands in proper fields. Sticker
            prices on DIY tools look lower because they skip the notes work; we
            add the labor cost back in below.
          </p>
        </div>

        {/* Three cost cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {/* Agency */}
          <div className="bg-rose-950/30 border border-rose-500/30 rounded-lg p-6 relative" data-testid="mc-calc-agency-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-rose-500/20 text-rose-300 flex items-center justify-center">
                <XIcon size={14} weight="bold" />
              </div>
              <h3 className="text-sm font-black text-rose-200 uppercase tracking-wider">Agency</h3>
            </div>
            <div className="text-3xl md:text-4xl font-black text-white mb-1" data-testid="mc-calc-agency-amount">
              {fmtMoney(numbers.agency)}
            </div>
            <p className="text-xs text-rose-200/70 leading-relaxed">
              $1,500 base + ~$0.35/contact for notes cleanup
            </p>
            <p className="text-[10px] text-rose-200/50 mt-2">
              4–12 week turnaround
            </p>
          </div>

          {/* DIY */}
          <div className="bg-slate-800/40 border border-slate-500/30 rounded-lg p-6 relative" data-testid="mc-calc-diy-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-slate-500/20 text-slate-300 flex items-center justify-center">
                <XIcon size={14} weight="bold" />
              </div>
              <h3 className="text-sm font-black text-slate-200 uppercase tracking-wider">DIY tool + labor</h3>
            </div>
            <div className="text-3xl md:text-4xl font-black text-white mb-1" data-testid="mc-calc-diy-amount">
              {fmtMoney(numbers.diy)}
            </div>
            <p className="text-xs text-slate-200/70 leading-relaxed">
              Import2 / MigrateMyCRM / Trujay base + manual notes cleanup
            </p>
            <p className="text-[10px] text-slate-200/50 mt-2">
              Notes copied as text — fields not extracted
            </p>
          </div>

          {/* MigrationCrunch */}
          <div className="bg-amber-500/10 border border-[#F39C12] rounded-lg p-6 relative ring-2 ring-[#F39C12]/30" data-testid="mc-calc-mc-card">
            <div className="absolute -top-2 -right-2 bg-[#F39C12] text-black text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest">
              You
            </div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-[#F39C12]/30 text-[#F39C12] flex items-center justify-center">
                <CheckCircle size={14} weight="fill" />
              </div>
              <h3 className="text-sm font-black text-[#F39C12] uppercase tracking-wider">MigrationCrunch</h3>
            </div>
            <div className="text-3xl md:text-4xl font-black text-white mb-1" data-testid="mc-calc-mc-amount">
              {fmtMoney(numbers.mc)}
            </div>
            <p className="text-xs text-amber-100/80 leading-relaxed">
              {numbers.tier.name} tier · flat one-time
            </p>
            <p className="text-[10px] text-amber-100/60 mt-2">
              ~15 min · ~99% accuracy on notes
            </p>
          </div>
        </div>

        {/* Savings reveal */}
        <div className="bg-gradient-to-br from-[#F39C12] to-[#E08C00] rounded-lg p-6 md:p-10 text-center text-black shadow-2xl" data-testid="mc-calc-savings-block">
          <p className="text-xs uppercase tracking-[0.2em] font-black mb-2 opacity-70">
            You save vs the most expensive option
          </p>
          <div className="text-5xl md:text-7xl font-black mb-2" data-testid="mc-calc-savings-amount">
            {fmtMoney(numbers.savings)}
          </div>
          <p className="text-sm md:text-base font-bold opacity-80 mb-1">
            on {fmtCount(numbers.safe)} contacts — about{' '}
            <span className="underline decoration-2 underline-offset-2" data-testid="mc-calc-savings-pct">
              {numbers.savingsPct}% less
            </span>{' '}
            than the next cheapest option.
          </p>
          <p className="text-xs opacity-60 mb-6">
            And about 15 minutes instead of a month.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#hero"
              className="bg-black hover:bg-slate-900 text-white font-bold px-6 py-3 rounded inline-flex items-center gap-2 transition-colors"
              data-testid="mc-calc-cta-preview"
            >
              Try the free preview — 50 contacts + notes
              <ArrowRight size={16} weight="bold" />
            </a>
            <a
              href="#pricing"
              className="text-sm text-black/70 hover:text-black underline font-bold"
              data-testid="mc-calc-cta-pricing"
            >
              See all tiers →
            </a>
          </div>
        </div>

        {/* Expandable math disclosure — builds trust by showing our work */}
        <div className="mt-8 max-w-3xl mx-auto" data-testid="mc-calc-math-disclosure">
          <button
            type="button"
            onClick={() => setShowMath((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F39C12]/30 rounded-md transition-colors text-left"
            data-testid="mc-calc-toggle-math"
            aria-expanded={showMath}
          >
            <span className="text-sm font-bold text-white inline-flex items-center gap-2">
              <WarningCircle size={16} weight="fill" className="text-[#F39C12]" />
              Why these numbers? Open the math.
            </span>
            {showMath ? (
              <CaretUp size={16} weight="bold" className="text-white/60" />
            ) : (
              <CaretDown size={16} weight="bold" className="text-white/60" />
            )}
          </button>

          {showMath && (
            <div className="bg-white/[0.03] border border-white/10 border-t-0 rounded-b-md px-5 py-5 mt-[-1px] space-y-5 text-sm text-white/75 leading-relaxed" data-testid="mc-calc-math-body">
              <p>
                <strong className="text-white">Big savings claims deserve big scrutiny.</strong>{' '}
                Here&apos;s exactly how we arrive at each number — and why the
                comparison is fair even though competitor sticker prices can
                look lower than ours.
              </p>

              {/* The key trap */}
              <div className="bg-amber-500/10 border-l-2 border-[#F39C12] px-4 py-3 rounded-r">
                <p className="text-xs uppercase tracking-widest text-[#F39C12] font-black mb-1">
                  The trap
                </p>
                <p className="text-sm text-white/85">
                  Tools like <strong className="text-white">MigrateMyCRM</strong> publish
                  prices as low as $0.10–$0.30/contact and{' '}
                  <strong className="text-white">Import2</strong> starts around $499.
                  Their sticker prices ARE lower than ours. But those tools only
                  migrate <em>structured</em> fields — Name → Name, Email →
                  Email. They dump the Notes column as a single text blob in
                  the new CRM, leaving 30–40% of your relationship
                  intelligence stranded.
                </p>
                <p className="text-sm text-white/85 mt-2">
                  To get the <em>same outcome</em> MigrationCrunch delivers,
                  you (or a VA) then have to read every note and re-enter the
                  phone numbers, family info, AUM amounts, and deal context by
                  hand. <strong className="text-white">That labor is the
                  hidden bill</strong> — and it&apos;s what makes our
                  $599 flat fee actually cheaper end-to-end.
                </p>
              </div>

              {/* Formula 1 — agency */}
              <div>
                <p className="text-xs uppercase tracking-widest text-rose-300 font-black mb-2">
                  Agency &mdash; how we computed it
                </p>
                <p className="text-sm">
                  Formula: <code className="bg-white/10 px-1 py-0.5 rounded text-[12px]">$1,500 base + $0.35 × contact count</code>, soft-capped at $40,000.
                </p>
                <p className="text-xs text-white/55 mt-1">
                  Anchored in actual 2025/2026 SMB consulting quotes. Trujay&apos;s
                  white-glove service starts ~$1,200 for 1k records. CloudFuze
                  custom migrations land $3k–$15k for mid-market. Independent
                  CRM consultants charge $100–$200/hr and quote 40–100 hours
                  for a note-extraction-included migration. We picked the
                  middle of that range.
                </p>
              </div>

              {/* Formula 2 — DIY */}
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-300 font-black mb-2">
                  DIY tool + cleanup &mdash; how we computed it
                </p>
                <p className="text-sm">
                  Formula: <code className="bg-white/10 px-1 py-0.5 rounded text-[12px]">$499 base + $0.20 × contact count</code> &mdash; no cap.
                </p>
                <p className="text-xs text-white/55 mt-1">
                  Modeled on actual per-record pricing from the three biggest
                  DIY migration vendors:{' '}
                  <strong className="text-white/80">Import2</strong> ~$499 base + per-record tier,{' '}
                  <strong className="text-white/80">MigrateMyCRM</strong> ~$0.10–$0.30
                  <em> per record</em> (every email, note, and task counts — so a
                  contact with 8 records-of-history costs them 8× more than the
                  contact alone), and{' '}
                  <strong className="text-white/80">Trujay Express</strong> ~$899 base + per-record fees.
                  We blend these into a per-contact average of $0.20 (which
                  assumes ~3–8 records per contact, the typical SMB load). The
                  formula scales linearly past 100k contacts because real
                  quotes do too — no artificial cap. Most importantly, these
                  tools <em>don&apos;t extract fields from your notes column</em>;
                  they copy the notes column as a single text blob. The
                  cleanup labor to manually re-enter the phone numbers, deal
                  details, and family info hidden in those notes is the
                  hidden bill — see &ldquo;The trap&rdquo; above.
                </p>
              </div>

              {/* Formula 3 — MC */}
              <div>
                <p className="text-xs uppercase tracking-widest text-[#F39C12] font-black mb-2">
                  MigrationCrunch &mdash; the actual price
                </p>
                <p className="text-sm">
                  Flat fee from our <a href="#pricing" className="underline text-amber-300 hover:text-amber-200">published pricing page</a>:
                  $599 (up to 12,500), $1,799 (≤100k), $4,499 (≤500k), $7,999 (unlimited).
                </p>
                <p className="text-xs text-white/55 mt-1">
                  The notes-extraction is built into that flat fee — no
                  cleanup labor, no per-record surcharge, no surprise invoice.
                  This is the entire bill end-to-end.
                </p>
              </div>

              {/* Honesty footer */}
              <p className="text-xs text-white/50 italic border-t border-white/10 pt-4">
                Real quotes vary based on data complexity, source CRM, and
                team. These are conservative middle-of-road estimates designed
                to help you compare paths, not exact quotes. If you want a
                concrete number,{' '}
                <a href="mailto:chip@migrationcrunch.com" className="underline hover:text-white">
                  email Chip
                </a>{' '}
                with your contact count and source CRM — he&apos;ll respond
                with an honest fit assessment within hours.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
